import React, { useState } from 'react';

import MyButton from '../../../../shared-components/button/Button';
import CustomInput from '../../../../shared-components/custom-input/CustomInput';
import CustomPhoneInput from '../../../../shared-components/custom-input/CustomPhoneInput';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SelectField from '../../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import deviceApi from '../../../../utils/api/device-hub';

const BRAND_COUNTRY_OPTIONS = [
  { value: 'IN', label: 'India' },
  { value: 'US', label: 'United States' },
  { value: 'SA', label: 'Saudi' },
  { value: 'UAE', label: 'United Arab Emirates' },
];

const BRAND_COUNTRY_PHONE_CODES: Record<string, string> = {
  IN: '91',
  US: '1',
  SA: '966',
  UAE: '971',
};

/**
 * Returns the default dialing code for a given brand country.
 * @param {any} country - The ISO country code.
 */
function getCountryCodeForBrandCountry(country?: string): string {
  if (!country) return '91';
  return BRAND_COUNTRY_PHONE_CODES[country] || '91';
}

interface AddBrandDrawerProperties {
  /** Whether the modal is open */
  open: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /**
   * Called after a brand is successfully created.
   * @param brandId - The _id of the newly created brand
   */
  onBrandCreated: (brandId: string) => void;
}

/**
 * Slide-in drawer modal for adding a new brand.
 * @param {any} param0 - The param0
 * @param {any} param0.open - The open prop
 * @param {any} param0.onClose - The onClose prop
 * @param {any} param0.onBrandCreated - The onBrandCreated prop
 */
function AddBrandDrawer({ open, onClose, onBrandCreated }: AddBrandDrawerProperties) {
  const [companyName, setCompanyName] = useState('');
  const [brandName, setBrandName] = useState('');
  const [country, setCountry] = useState('');
  const [countryCode, setCountryCode] = useState('91');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [companyError, setCompanyError] = useState('');
  const [brandError, setBrandError] = useState('');
  const [countryError, setCountryError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFormErrors, setShowFormErrors] = useState(false);

  /**
   * Resets all form fields to their initial state.
   */
  const resetForm = () => {
    setCompanyName('');
    setBrandName('');
    setCountry('');
    setCountryCode('91');
    setEmail('');
    setPhone('');
    setCompanyError('');
    setBrandError('');
    setCountryError('');
    setPhoneError('');
    setEmailError('');
    setShowFormErrors(false);
  };

  /**
   * Handles closing the modal and resetting the form.
   */
  const handleClose = () => {
    resetForm();
    onClose();
  };

  /**
   * Handles phone number and country code field changes.
   * @param {any} name - Field name ('countryCode' or phone number field)
   * @param {any} value - The updated value
   */
  const handlePhoneChange = (name: string, value: string | number) => {
    if (name === 'countryCode') {
      setCountryCode(value.toString());
    } else {
      setPhone(value.toString());
    }
    if (phoneError) setPhoneError('');
  };

  /**
   * Handles country selection and syncs the dialing code.
   * @param {any} selectedCountry - ISO country code
   */
  const handleCountryChange = (selectedCountry: string) => {
    setCountry(selectedCountry);
    setCountryCode(getCountryCodeForBrandCountry(selectedCountry));
    if (countryError) setCountryError('');
  };

  /**
   * Validates and submits the brand creation form.
   */
  const handleSubmit = () => {
    setShowFormErrors(true);
    setCompanyError('');
    setBrandError('');
    setCountryError('');
    setPhoneError('');
    setEmailError('');

    if (!companyName.trim()) {
      return;
    }
    if (!brandName.trim()) {
      return;
    }
    if (!country) {
      return;
    }
    if (!email.trim()) {
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return;
    }

    if (phone.trim() && (phone.trim().length < 10 || phone.trim().length > 15)) {
      setPhoneError('Phone number must be between 10 to 15 digits');
      return;
    }

    setSubmitting(true);

    const payload = {
      companyName: companyName.trim(),
      name: brandName.trim(),
      country: BRAND_COUNTRY_OPTIONS.find((opt) => opt.value === country)?.label || country,
      email: email.trim(),
      ...(phone.trim() ? { phoneNumber: phone.trim(), countryCode: countryCode.trim() } : {}),
    };

    deviceApi
      .createBrand(payload)
      .then((response) => {
        const newBrand = response.data?.results?.[0] || response.data || {};
        const brandId = newBrand._id || newBrand.id || '';
        onBrandCreated(brandId);
        setShowSuccess(true);
      })
      .catch((error) => {
        const message =
          error?.response?.data?.message || error?.message || 'Failed to create brand';
        const lowerMessage = message.toLowerCase();

        if (
          lowerMessage.includes('phone') ||
          lowerMessage.includes('digit') ||
          lowerMessage.includes('mobile')
        ) {
          setPhoneError(message);
        } else if (
          lowerMessage.includes('brand') ||
          lowerMessage.includes('name already taken') ||
          lowerMessage.includes('name already exists')
        ) {
          setBrandError(message);
        } else if (lowerMessage.includes('company')) {
          setCompanyError(message);
        } else if (lowerMessage.includes('email') || lowerMessage.includes('mail')) {
          setEmailError(message);
        } else if (lowerMessage.includes('country')) {
          setCountryError(message);
        } else {
          setBrandError(message);
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <>
      <ModalBox
        open={open}
        title=""
        content={
          <div className="left-drawer-content drawer-wide">
            <div className="drawer-header">
              <h3 className="drawer-title">Add Brand</h3>
              <p className="drawer-subtitle">
                Create a new brand to associate with charging devices.
              </p>
            </div>
            <div className="drawer-body">
              <div className="brand-fields-card">
                {/* Row 1 */}
                <div className="drawer-grid-2">
                  <CustomInput
                    type="text"
                    name="companyName"
                    inputLabel="Company Name"
                    placeholder="Enter Company Name"
                    value={companyName}
                    onChangeValue={(_, value) => {
                      setCompanyName(value.toString());
                      if (companyError) setCompanyError('');
                    }}
                    error={Boolean((showFormErrors && !companyName.trim()) || companyError)}
                    errorMessage={companyError || 'Company Name is required'}
                    valueRequired
                    characterLimit={100}
                  />

                  <CustomInput
                    type="text"
                    name="brandName"
                    inputLabel="Brand Name"
                    placeholder="Enter Brand Name"
                    value={brandName}
                    onChangeValue={(_, value) => {
                      setBrandName(value.toString());
                      if (brandError) setBrandError('');
                    }}
                    error={Boolean((showFormErrors && !brandName.trim()) || brandError)}
                    errorMessage={brandError || 'Brand Name is required'}
                    valueRequired
                    characterLimit={100}
                  />
                </div>

                {/* Row 2 */}
                <SelectField
                  name="brandCountry"
                  inputLabel="Country"
                  options={BRAND_COUNTRY_OPTIONS}
                  placeholder="Choose Country"
                  selectedValue={country}
                  onChangeValue={(_, value) => handleCountryChange(value.toString())}
                  error={Boolean((showFormErrors && !country) || countryError)}
                  errorMessage={countryError || 'Country is required'}
                  valueRequired
                  enableSearch
                />

                {/* Row 3 */}
                <CustomPhoneInput
                  name="phoneNumber"
                  inputLabel="Phone Number (Optional)"
                  placeholder="Enter Mobile Number"
                  type="number"
                  value={phone}
                  value1={countryCode}
                  onChangeValue={handlePhoneChange}
                  disableDropdown
                  error={Boolean(phoneError)}
                  errorMessage={phoneError}
                />

                {/* Row 4 */}
                <CustomInput
                  type="email"
                  name="contactEmail"
                  inputLabel="Email"
                  placeholder="Enter Email Id"
                  value={email}
                  onChangeValue={(_, value) => {
                    setEmail(value.toString());
                    if (emailError) setEmailError('');
                  }}
                  error={
                    Boolean(
                      (showFormErrors &&
                        (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))) ||
                        emailError,
                    )
                  }
                  errorMessage={
                    emailError ||
                    (!email.trim() ? 'Email is required' : 'Please enter a valid email address')
                  }
                  valueRequired
                />
              </div>
            </div>
            <div className="drawer-footer">
              <MyButton label="Cancel" buttonType="error" onClickFunc={handleClose} />
              <MyButton
                label="Save Brand"
                buttonType="submit"
                onClickFunc={handleSubmit}
                disableButton={submitting}
                loading={submitting}
              />
            </div>
          </div>
        }
        closeModal={handleClose}
        header={false}
        position="right"
        modalBoxAnimation="modalAnimateFromRight"
      />
      <ModalBox
        open={showSuccess}
        title=""
        content={
          <SuccessNotification
            title="Brand Added Successfully"
            description="New brand has been added successfully."
            onNotificationClose={() => {
              setShowSuccess(false);
              resetForm();
              onClose();
            }}
          />
        }
        closeModal={() => {
          setShowSuccess(false);
          resetForm();
          onClose();
        }}
        header={false}
        notification
      />
    </>
  );
}

export default AddBrandDrawer;
